import { translate } from '../../../../../common/maths/geometry/point';
import { Point } from '../../../../../common/maths/geometry/pointTypes';
import { clampPanToBounds, ImageModalPanEdgeSession } from './imageModalPanBoundsUtils';
import { getImageModalZoomScaleDeltaFromWheelDelta, getNextZoomScale } from './imageModalZoomScaleUtils';
import { getZoomTranslationForScale, isViewportCentreAnchor, isZeroTranslation } from './imageModalZoomGeometryUtils';
import { ImageModalZoomBounds, ImageModalZoomDimensions, ImageModalZoomState } from './imageModalZoomTypes';

/**
 * Calculates the next zoom state for a scale change anchored to a viewport point.
 */
export const getNextZoomState = ({
    anchorPoint,
    bounds,
    dimensions,
    scaleDelta,
    zoomState,
}: {
    anchorPoint: Point;
    bounds?: ImageModalZoomBounds;
    dimensions: ImageModalZoomDimensions;
    scaleDelta: number;
    zoomState: ImageModalZoomState;
}): ImageModalZoomState => {
    const nextScale = getNextZoomScale({
        currentScale: zoomState.scale,
        scaleDelta,
        minScale: bounds?.minScale ?? 1,
        maxScale: bounds?.maxScale ?? 4,
    });

    if (nextScale === zoomState.scale) return zoomState;

    const nextTranslation = getZoomTranslationForScale({
        anchorPoint,
        currentScale: zoomState.scale,
        currentTranslation: zoomState.translation,
        nextScale,
        viewportSize: dimensions.viewportSize,
    });
    const isRestZoomOut =
        isViewportCentreAnchor(anchorPoint, dimensions.viewportSize) &&
        nextScale < zoomState.scale &&
        !isZeroTranslation(zoomState.translation);
    const isZoomIn = nextScale > zoomState.scale;
    const skipPanClamp = isRestZoomOut || isZoomIn;

    return {
        scale: nextScale,
        translation: skipPanClamp
            ? nextTranslation
            : clampPanToBounds(nextTranslation, {
                  scale: nextScale,
                  ...dimensions,
              }),
    };
};

/**
 * Calculates the next anchored zoom state from the latest zoom state and a wheel delta.
 */
export const getNextZoomStateFromWheelDelta = ({
    anchorPoint,
    bounds,
    dimensions,
    wheelDeltaY,
    zoomState,
}: {
    anchorPoint: Point;
    bounds?: ImageModalZoomBounds;
    dimensions: ImageModalZoomDimensions;
    wheelDeltaY: number;
    zoomState: ImageModalZoomState;
}): ImageModalZoomState => {
    const scaleDelta = getImageModalZoomScaleDeltaFromWheelDelta(wheelDeltaY, zoomState.scale, bounds);
    if (!scaleDelta) return zoomState;

    return getNextZoomState({
        anchorPoint,
        bounds,
        dimensions,
        scaleDelta,
        zoomState,
    });
};

/**
 * Calculates the next zoom state after a pan delta has been applied.
 */
export const getNextPannedZoomState = ({
    dimensions,
    panDelta,
    panEdgeSession = null,
    zoomState,
}: {
    dimensions: ImageModalZoomDimensions;
    panDelta: Point;
    panEdgeSession?: ImageModalPanEdgeSession | null;
    zoomState: ImageModalZoomState;
}): ImageModalZoomState => {
    const nextTranslation = translate(panDelta, zoomState.translation);

    return {
        ...zoomState,
        translation: clampPanToBounds(
            nextTranslation,
            {
                scale: zoomState.scale,
                ...dimensions,
            },
            { panEdgeSession },
        ),
    };
};
